import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Stack, Tooltip } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import EnrollmentOccurrencePointForm from './EnrollmentOccurrencePointForm';
import EnrollmentSummaryCount from './EnrollmentSummaryCount';
import EntryExitDatesWithAssessmentLinks from './EntryExitDatesWithAssessmentLinks';
import {
  CommonDetailGridContainer,
  CommonDetailGridItem,
} from '@/components/elements/CommonDetailGrid';
import Loading from '@/components/elements/Loading';
import NotCollectedText from '@/components/elements/NotCollectedText';

import RouterLink from '@/components/elements/RouterLink';
import useAuth from '@/modules/auth/hooks/useAuth';
import { parseOccurrencePointFormDefinition } from '@/modules/form/util/formUtil';
import EnrollmentStatus from '@/modules/hmis/components/EnrollmentStatus';
import HmisEnum from '@/modules/hmis/components/HmisEnum';
import { parseAndFormatDate, yesNo } from '@/modules/hmis/hmisUtil';
import { DashboardEnrollment } from '@/modules/hmis/types';
import { ProjectDashboardRoutes } from '@/routes/routes';
import { HmisEnums } from '@/types/gqlEnums';
import { Destination } from '@/types/gqlTypes';
import { generateSafePath } from '@/utils/pathEncoding';

const EnrollmentDetails = ({
  enrollment,
}: {
  enrollment: DashboardEnrollment;
}) => {
  const { user } = useAuth();
  const rows = useMemo(() => {
    const content: Record<string, ReactNode> = {};
    // If enrollment is incomplete, show that first
    if (enrollment.inProgress) {
      content['Enrollment Status'] = (
        <EnrollmentStatus enrollment={enrollment} />
      );
    }

    // Entry - Exit date, with assmt links to change them
    content['Entry / Exit Dates'] = (
      <EntryExitDatesWithAssessmentLinks enrollment={enrollment} />
    );

    // Exit Destination
    if (enrollment.exitDate) {
      content['Exit Destination'] = (
        <HmisEnum
          value={enrollment.exitDestination || Destination.DataNotCollected}
          enumMap={HmisEnums.Destination}
        />
      );
    }

    // Summary of open enrollments. Arr will be empty unless user has access to see summaries.
    if (enrollment && enrollment.openEnrollmentSummary.length > 0) {
      const title = 'Other Open Enrollments';
      content[title] = (
        <EnrollmentSummaryCount
          enrollmentSummary={enrollment.openEnrollmentSummary}
          clientId={enrollment.client.id}
        />
      );
    }

    // Occurrence point values (move in date, date of engagement, etc.)
    enrollment.occurrencePointForms.forEach(({ definition }) => {
      // Determine whether this form has any fields that  are editable.
      // Pass the user because there might be fields that are only editable by some users.
      const { displayTitle, isEditable, definitionForDisplay } =
        parseOccurrencePointFormDefinition(definition, user!);

      content[displayTitle] = (
        <EnrollmentOccurrencePointForm
          enrollment={enrollment}
          definition={definition}
          definitionForDisplay={definitionForDisplay}
          editable={isEditable && enrollment.access.canEditEnrollments}
          dialogTitle={displayTitle}
        />
      );
    });

    // CoC Code. Only show if project operates in multiple CoCs.
    if (enrollment.project.projectCocs.nodesCount > 1) {
      content['Enrollment CoC'] = enrollment.enrollmentCoc || (
        <NotCollectedText />
      );
    }

    // HUD Chronic field is hidden if it is Null, which indicates that the user lacks access can_view_hud_chronic_status, or that the client has no open enrollments.
    // HUD Chronic field is also hidden if the Enrollment is exited. (The Client may have a chronicity status from other open enrollments, but there is no need to display that on the Exited enrollment.)
    if (enrollment.client.hudChronic !== null && !enrollment.exitDate) {
      content['HUD Chronic'] = yesNo(enrollment.client.hudChronic);
    }

    if (
      enrollment.sourceReferralPosting &&
      enrollment.project.access.canManageIncomingReferrals
    ) {
      // Basic details about the referral. If this section needs more customization, it could be implemented
      // as a read-only occurrence point form.
      content['Referral Source'] = (
        <Stack direction='row' gap={1}>
          {`Referred from ${
            enrollment.sourceReferralPosting.referredFrom
          } on ${parseAndFormatDate(
            enrollment.sourceReferralPosting.referralDate
          )}`}
          <RouterLink
            to={generateSafePath(ProjectDashboardRoutes.REFERRAL_POSTING, {
              projectId: enrollment.project.id,
              referralPostingId: enrollment.sourceReferralPosting.id,
            })}
            openInNew
          >
            View Referral
          </RouterLink>
        </Stack>
      );
    }

    const tooltips: Record<string, ReactNode> = {
      'HUD Chronic': (
        <>
          Indicates whether the client is currently considered chronically
          homeless, based on the HUD definition for Chronic at Point-In-Time.
          <br />
          <br />A client qualifies as chronically homeless if they have a
          disabling condition and have been continuously homeless for at least
          12 months, or have experienced at least four episodes of homelessness
          in the past three years, totaling 12 or more months.
        </>
      ),
    };

    return Object.entries(content).map(([id, value], index) => ({
      id: String(index),
      label: tooltips[id] ? (
        <>
          {id}{' '}
          <Tooltip title={tooltips[id]}>
            <HelpOutlineIcon
              fontSize='small'
              sx={{ verticalAlign: 'bottom', ml: 1, color: 'text.secondary' }}
            />
          </Tooltip>
        </>
      ) : (
        id
      ),
      value,
    }));
  }, [enrollment, user]);

  if (!enrollment || !rows) return <Loading />;

  return (
    <CommonDetailGridContainer>
      {rows.map(({ id, label, value }) => (
        <CommonDetailGridItem label={label} key={id}>
          {value}
        </CommonDetailGridItem>
      ))}
    </CommonDetailGridContainer>
  );
};

export default EnrollmentDetails;
